import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

export interface IStatusItem {
  group_name: string;
  count: number;
  status: string;
  displayTitle: boolean;
  lastInGroup: boolean;
}

export interface IStatus {
  status: string;
  count: number;
}
export interface IGroup {
  group_name: string;
  statuses: IStatus[];
}

const getStatusRoute = (status: string, user_type: string) => {
  const routes: Record<string, string> = {
    Pending: "/quick-requests?display_status=pending&page=1",
    "Pending quotation": "quote-requests?display_status=active&page=1",
    Open: "/quote-requests?display_status=open_for_quotations&page=1",
    "Pending approval": "/quote-requests?display_status=approval_pending&page=1",
    Quoted:
      user_type === "shipper"
        ? "/quote-requests?display_status=awaiting_action&page=1"
        : "/quote-requests?display_status=pending&page=1",
    "Pending revalidation": "/quote-requests?display_status=confirmation_required&page=1",
    Booked: "/shipments?display_status=Booked&page=1",
    "In transit": "/shipments?display_status=In%20transit",
    Delayed: "/shipments?display_status=Delayed&page=1",
    Incomplete: "/shipments?display_status=Incomplete&page=1",
    "Pending submission": "/invoices?page=1&status=Unsubmitted",
    Declined: "/invoices?page=1",
    "Pending audit": "/invoices?display_status=awaiting_audit&page=1",
  };

  return routes[status];
};

// Splits items into rows based on the maximum number of columns per row and rearranges the data to optimize layout.
const distributeItemsIntoRows = (items: IStatusItem[], maxColumns: number): IStatusItem[][] => {
  const groupedItems: IStatusItem[][] = [];
  const groupMap: Record<string, IStatusItem[]> = {};

  // Group items by group_name
  items.forEach((item) => {
    if (!groupMap[item.group_name]) {
      groupMap[item.group_name] = [];
    }
    groupMap[item.group_name].push(item);
  });

  // Convert group map to an array of grouped items
  Object.values(groupMap).forEach((group) => groupedItems.push(group));

  const rows: IStatusItem[][] = [];

  // Function to find the best row to place a group without exceeding maxColumns
  const findBestRowForGroup = (group: IStatusItem[]): number | null => {
    const bestRowIndex = rows.findIndex((row) => row.length + group.length <= maxColumns);
    return bestRowIndex !== -1 ? bestRowIndex : null;
  };

  groupedItems.forEach((group) => {
    const bestRowIndex = findBestRowForGroup(group);

    if (bestRowIndex !== null) {
      // Place the group in the best fitting row
      rows[bestRowIndex].push(...group);
    } else {
      // Create a new row if no existing row can accommodate the group
      rows.push([...group]);
    }
  });

  return rows;
};

// Flattens grouped data into a single array and adds metadata for display and layout.
const flattenData = (data: IGroup[]): IStatusItem[] =>
  data.flatMap((group) =>
    group.statuses.map((status, index) => ({
      group_name: group.group_name,
      count: status.count,
      status: status.status,
      displayTitle: index === 0,
      lastInGroup: index === group.statuses.length - 1,
    })),
  );

const formatString = (input: string): string => {
  const formatted = input.toLowerCase().replace(/_/g, " ");
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

function mapGroupNameToTranslationKey(groupName: string): ReactNode {
  const translations: Record<string, ReactNode> = {
    "Quick requests": <FormattedMessage id="dashboard.widgets.groupName.quickRequest" defaultMessage="Quick requests" />,
    "Quote request": <FormattedMessage id="dashboard.widgets.groupName.quoteRequest" defaultMessage="Quote request" />,
    Shipments: <FormattedMessage id="dashboard.widgets.groupName.shipments" defaultMessage="Shipments" />,
    Invoices: <FormattedMessage id="dashboard.widgets.groupName.invoices" defaultMessage="Invoices" />,
  };

  return translations[groupName] || groupName;
}

function mapStatusToTranslationKey(status: string): ReactNode {
  const translations: Record<string, ReactNode> = {
    Open: <FormattedMessage id="dashboard.widgets.status.open" defaultMessage="Open" />,
    Pending: <FormattedMessage id="dashboard.widgets.status.pending" defaultMessage="Pending" />,
    "Pending approval": <FormattedMessage id="dashboard.widgets.status.pendingApproval" defaultMessage="Pending approval" />,
    Quoted: <FormattedMessage id="dashboard.widgets.status.quoted" defaultMessage="Quoted" />,
    "Pending booking": <FormattedMessage id="dashboard.widgets.status.pendingBooking" defaultMessage="Pending booking" />,
    Booked: <FormattedMessage id="dashboard.widgets.status.booked" defaultMessage="Booked" />,
    "In transit": <FormattedMessage id="dashboard.widgets.status.inTransit" defaultMessage="In transit" />,
    Delayed: <FormattedMessage id="dashboard.widgets.status.delayed" defaultMessage="Delayed" />,
    "Pending audit": <FormattedMessage id="dashboard.widgets.status.pendingAudit" defaultMessage="Pending audit" />,
    "Pending quotation": <FormattedMessage id="tabs.pendingQuotation" defaultMessage="Pending quotation" />,
    "Pending revalidation": <FormattedMessage id="tabs.pendingRevalidation" defaultMessage="Pending revalidation" />,
    "Pending submission": <FormattedMessage id="tabs.pendingSubmission" defaultMessage="Pending submission" />,
    Declined: <FormattedMessage id="tabs.declined" defaultMessage="Declined" />,
    Incomplete: <FormattedMessage id="tabs.incomplete" defaultMessage="Incomplete" />,
  };

  return translations[status] || status;
}

export {
  getStatusRoute,
  distributeItemsIntoRows,
  flattenData,
  formatString,
  mapGroupNameToTranslationKey,
  mapStatusToTranslationKey,
};
